import type { MapCoordinates } from '@freelancer/datastore';

export interface TransportRoute {
  start: string;
  end: string;
  link: string;
  displayText?: string;
  startLocation?: MapCoordinates;
  endLocation?: MapCoordinates;
}

export enum State {
  ADELAIDE = 'Adelaide',
  BRISBANE = 'Brisbane',
  PERTH = 'Perth',
  SYDNEY = 'Sydney',
  MELBOURNE = 'Melbourne',
}

interface StateInformation {
  mapCoordinates: MapCoordinates;
  displayText?: string;
}
export const STATE_DATA_MAPPING = new Map<State, StateInformation>([
  [
    State.SYDNEY,
    {
      mapCoordinates: {
        latitude: -33.835_301_642_191_27,
        longitude: 151.065_596_069_181_85,
      },
      displayText: $localize`Sydney is Australia's largest city featuring a busy ocean port. Thousands of loads of freight are shipped from Sydney to destinations across Australia every day.\n \n An important route used to ship goods is from Sydney to Perth - a journey of 3933 km which takes approximately 41 driving hours to complete. The most popular type of freight to be shipped from Sydney to Perth are cars, mobile machinery, camping caravans, a variety of full and partial loads of general freight, and shipping containers / trailers.`,
    },
  ],
  [
    State.MELBOURNE,
    {
      mapCoordinates: {
        latitude: -37.816_177_596_672_6,
        longitude: 144.967_963_553_540_13,
      },
      displayText: $localize`Melbourne is Australia's 2nd largest city, and as with most cities in Australia has a busy ocean port. Thousands of loads of freight are shipped from Melbourne to destinations across Australia every day. \n\n An important route used to ship goods is from Melbourne to Adelaide - a journey of 727 km which takes approximately 8 hours to complete. The most popular type of freight to be shipped from Melbourne to Adelaide is general freight, mobile machinery, cars, full loads of pallets and shipping containers / trailers.`,
    },
  ],
  [
    State.PERTH,
    {
      mapCoordinates: {
        latitude: -31.930_264_033_993_502,
        longitude: 115.865_375_150_773_84,
      },
      displayText: $localize`Perth is Australia's 4th largest city, and as with most cities in Australia has a busy ocean port. Perth is unique in that it is the largest city for thousands of kilometres to the North, South and East. Thousands of loads of freight are shipped from Perth to destinations across Australia every day and provides important supplies to rural towns and farms across Western Australia.\n \n An important route used to ship goods is from Perth to Sydney - a journey of 3934 km which takes approximately 42 driving hours to complete. \n\n The most popular type of freight to be shipped from Perth to Sydney are cars, mobile machinery, and a variety of full and partial loads of general freight.`,
    },
  ],
  [
    State.BRISBANE,
    {
      mapCoordinates: {
        latitude: -27.451_348_719_971_154,
        longitude: 153.028_870_234_445_66,
      },
      displayText: $localize`Brisbane is Australia's 3rd most populous city and has one of Australia's most important ocean ports with thousands of shipments to and from Asia, Indonesia, North and South America every day! \n\n The freight route used to ship goods from Brisbane for Melbourne is one of the busiest in the country and is a journey of 1668 km which takes approximately 18.5 driving hours to complete. The most popular type of freight to be shipped from Brisbane to Melbourne is mobile machinery, cars, full and partial loads of general freight, and shipping containers / trailers.`,
    },
  ],
  [
    State.ADELAIDE,
    {
      mapCoordinates: {
        latitude: -34.928_265_191_004_58,
        longitude: 138.600_986_771_195_8,
      },
      displayText: $localize`Adelaide is Australia's 5th largest city. Its position on the Australian continent makes it an important hub for transport of goods across Australia with both East-West and North-South routes passing through the city. Thousands of loads of freight are shipped from Adelaide to destinations across Australia every day. \n  \n The route used to ship goods from Adelaide to Melbourne is a journey of 727 km which takes approximately 8 hours to complete. The most popular type of freight to be shipped from Adelaide to Melbourne is cars, mobile machinery, general freight, shipping containers / trailers and caravans.`,
    },
  ],
]);

export const TRANSPORT_ROUTES: readonly TransportRoute[] = [
  {
    start: State.ADELAIDE,
    end: State.MELBOURNE,
    link: 'adelaide-to-melbourne',
    displayText: STATE_DATA_MAPPING.get(State.ADELAIDE)?.displayText,
    startLocation: STATE_DATA_MAPPING.get(State.ADELAIDE)?.mapCoordinates,
    endLocation: STATE_DATA_MAPPING.get(State.MELBOURNE)?.mapCoordinates,
  },
  {
    start: State.ADELAIDE,
    end: State.SYDNEY,
    link: 'adelaide-to-sydney',
    displayText: STATE_DATA_MAPPING.get(State.ADELAIDE)?.displayText,
    startLocation: STATE_DATA_MAPPING.get(State.ADELAIDE)?.mapCoordinates,
    endLocation: STATE_DATA_MAPPING.get(State.SYDNEY)?.mapCoordinates,
  },
  {
    start: State.BRISBANE,
    end: State.MELBOURNE,
    link: 'brisbane-to-melbourne',
    displayText: STATE_DATA_MAPPING.get(State.BRISBANE)?.displayText,
    startLocation: STATE_DATA_MAPPING.get(State.BRISBANE)?.mapCoordinates,
    endLocation: STATE_DATA_MAPPING.get(State.MELBOURNE)?.mapCoordinates,
  },
  {
    start: State.BRISBANE,
    end: State.SYDNEY,
    link: 'brisbane-to-sydney',
    displayText: STATE_DATA_MAPPING.get(State.BRISBANE)?.displayText,
    startLocation: STATE_DATA_MAPPING.get(State.BRISBANE)?.mapCoordinates,
    endLocation: STATE_DATA_MAPPING.get(State.SYDNEY)?.mapCoordinates,
  },
  {
    start: State.MELBOURNE,
    end: State.ADELAIDE,
    link: 'melbourne-to-adelaide',
    displayText: STATE_DATA_MAPPING.get(State.MELBOURNE)?.displayText,
    startLocation: STATE_DATA_MAPPING.get(State.MELBOURNE)?.mapCoordinates,
    endLocation: STATE_DATA_MAPPING.get(State.ADELAIDE)?.mapCoordinates,
  },
  {
    start: State.MELBOURNE,
    end: State.BRISBANE,
    link: 'melbourne-to-brisbane',
    displayText: STATE_DATA_MAPPING.get(State.MELBOURNE)?.displayText,
    startLocation: STATE_DATA_MAPPING.get(State.MELBOURNE)?.mapCoordinates,
    endLocation: STATE_DATA_MAPPING.get(State.BRISBANE)?.mapCoordinates,
  },
  {
    start: State.MELBOURNE,
    end: State.PERTH,
    link: 'melbourne-to-perth',
    displayText: STATE_DATA_MAPPING.get(State.MELBOURNE)?.displayText,
    startLocation: STATE_DATA_MAPPING.get(State.MELBOURNE)?.mapCoordinates,
    endLocation: STATE_DATA_MAPPING.get(State.PERTH)?.mapCoordinates,
  },
  {
    start: State.MELBOURNE,
    end: State.SYDNEY,
    link: 'melbourne-to-sydney',
    displayText: STATE_DATA_MAPPING.get(State.MELBOURNE)?.displayText,
    startLocation: STATE_DATA_MAPPING.get(State.MELBOURNE)?.mapCoordinates,
    endLocation: STATE_DATA_MAPPING.get(State.SYDNEY)?.mapCoordinates,
  },
  {
    start: State.PERTH,
    end: State.MELBOURNE,
    link: 'perth-to-melbourne',
    displayText: STATE_DATA_MAPPING.get(State.PERTH)?.displayText,
    startLocation: STATE_DATA_MAPPING.get(State.PERTH)?.mapCoordinates,
    endLocation: STATE_DATA_MAPPING.get(State.MELBOURNE)?.mapCoordinates,
  },
  {
    start: State.PERTH,
    end: State.SYDNEY,
    link: 'perth-to-sydney',
    displayText: STATE_DATA_MAPPING.get(State.PERTH)?.displayText,
    startLocation: STATE_DATA_MAPPING.get(State.PERTH)?.mapCoordinates,
    endLocation: STATE_DATA_MAPPING.get(State.SYDNEY)?.mapCoordinates,
  },
  {
    start: State.SYDNEY,
    end: State.ADELAIDE,
    link: 'sydney-to-adelaide',
    displayText: STATE_DATA_MAPPING.get(State.SYDNEY)?.displayText,
    startLocation: STATE_DATA_MAPPING.get(State.SYDNEY)?.mapCoordinates,
    endLocation: STATE_DATA_MAPPING.get(State.ADELAIDE)?.mapCoordinates,
  },
  {
    start: State.SYDNEY,
    end: State.BRISBANE,
    link: 'sydney-to-brisbane',
    displayText: STATE_DATA_MAPPING.get(State.SYDNEY)?.displayText,
    startLocation: STATE_DATA_MAPPING.get(State.SYDNEY)?.mapCoordinates,
    endLocation: STATE_DATA_MAPPING.get(State.BRISBANE)?.mapCoordinates,
  },
  {
    start: State.SYDNEY,
    end: State.MELBOURNE,
    link: 'sydney-to-melbourne',
    displayText: STATE_DATA_MAPPING.get(State.SYDNEY)?.displayText,
    startLocation: STATE_DATA_MAPPING.get(State.SYDNEY)?.mapCoordinates,
    endLocation: STATE_DATA_MAPPING.get(State.MELBOURNE)?.mapCoordinates,
  },
  {
    start: State.SYDNEY,
    end: State.PERTH,
    link: 'sydney-to-perth',
    displayText: STATE_DATA_MAPPING.get(State.SYDNEY)?.displayText,
    startLocation: STATE_DATA_MAPPING.get(State.SYDNEY)?.mapCoordinates,
    endLocation: STATE_DATA_MAPPING.get(State.PERTH)?.mapCoordinates,
  },
];
